import * as React from 'react';
import Stack from '@mui/material/Stack';
import Button, { ButtonProps } from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Slide from '@mui/material/Slide';
import zIndex from '@mui/material/styles/zIndex';
import { useTheme, alpha } from '@mui/material/styles';
import { TransitionProps } from '@mui/material/transitions';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Device from 'react-device-detect';

import { ImageUpload } from '@components';
import { refetchEventsVar } from '@context';
import { useUpsertEventImageMutation, EventImageTypeEnum, useRemoveEventImageMutation } from '@graphql';
import { DialogEditContext } from '../../common/layout';
import { coverValidationSchema } from './validationSchema';

interface EventCoverDialogProps {
  trigger?: React.ReactElement<ButtonProps>;
}

export function EventCoverDialog(props: Readonly<EventCoverDialogProps>) {
  const context = React.useContext(DialogEditContext);

  if (!context) {
    throw new Error('EventCoverDialog must be used inside DialogEditWrapper');
  }

  const { isEditMode, event } = context;
  const { trigger } = props;
  const [isOpen, setIsOpen] = React.useState(false);
  const { eventId } = useParams();
  const theme = useTheme();
  const { t } = useTranslation('field');

  const defaultValues = {
    cover: event?.covers?.[0]?.url ?? null,
  };

  const [upsertEventImage, { loading: upserting }] = useUpsertEventImageMutation({
    onCompleted: ({}) => {
      refetchEventsVar(true);
      handleClose();
      toast(t('event.toast.update.success', { ns: 'field' }), { type: 'success' });
    },
    onError(error) {
      toast.error(t('event.toast.update.error', { ns: 'field' }));
    },
  });

  const [removeEventImage, { loading: removing }] = useRemoveEventImageMutation({
    onCompleted: () => {
      refetchEventsVar(true);
      handleClose();
      toast(t('event.toast.remove.success', { ns: 'field' }), { type: 'success' });
    },
    onError(error) {
      toast.error(t('event.toast.remove.error', { ns: 'field' }));
    },
  });

  const formik = useFormik({
    initialValues: defaultValues,
    validationSchema: coverValidationSchema(t),
    onSubmit: async ({ cover }) => {
      const [existginCover] = event?.covers ?? [null];

      if (typeof cover === 'string' && cover === existginCover?.url) {
        // do nothing
        handleClose();
      } else if (cover === null && existginCover !== null) {
        // remove old cover
        await removeEventImage({
          variables: {
            id: existginCover.id,
          },
        });
      } else if (existginCover !== cover) {
        // update old cover or add new cover
        await upsertEventImage({
          variables: {
            eventId: eventId as string,
            type: EventImageTypeEnum.Cover,
            image: cover,
          },
        });
      }
    },
  });

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleSubmit = () => {
    formik.submitForm();
  };

  const handleClose = () => {
    setIsOpen(false);
    formik.resetForm({ values: defaultValues });
  };

  const handleImageSelected = (files: File[]) => {
    formik.setFieldValue('images', files);
  };

  React.useEffect(() => {
    // ensure the dialog is closed when switching to see mode
    if (!isEditMode) {
      setIsOpen(false);
    }
  }, [isEditMode]);

  return (
    <>
      {trigger && React.cloneElement(trigger, { onClick: handleOpen })}
      {!trigger && isEditMode && (
        <Button
          size="small"
          variant="outlined"
          color="uncolored"
          startIcon={<theme.icons.image />}
          sx={{
            position: 'absolute',
            right: 10,
            bottom: 10,
            zIndex: zIndex.appBar - 1,
            backdropFilter: 'blur(10px)',
            backgroundColor: alpha(theme.palette.background.default, 0.7),
          }}
          onClick={handleOpen}
        >
          {t('edit', { ns: 'cta' })}
        </Button>
      )}
      <Dialog
        open={isOpen}
        scroll="body"
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={Device.isMobileOnly}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, .6)',
            },
          },
          paper: {
            sx: {
              backgroundColor: 'theme.palette.background.default',
              border: Device.isMobile ? undefined : '1px solid rgba(255, 255, 255, 0.1)',
              pt: 'env(safe-area-inset-top)',
            },
          },
        }}
        slots={{
          transition: Transition,
        }}
      >
        <Stack height="100%" sx={{ position: 'relative' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" p={3}>
            <Typography variant="h6">{t(`event.title.${event?.id ? 'edit' : 'create'}`)}</Typography>
            <IconButton onClick={handleClose}>
              <theme.icons.close />
            </IconButton>
          </Stack>

          <form onSubmit={formik.handleSubmit}>
            <DialogContent>
              <Stack spacing={2}>
                <ImageUpload
                  label="Ajouter une couverture"
                  onImageProcessed={(file) => formik.setFieldValue('cover', file)}
                  onImageSelected={handleImageSelected}
                  initialPreview={formik.values.cover}
                />
              </Stack>
            </DialogContent>
          </form>
          <DialogActions>
            <Stack direction="row" spacing={2} p={3} width="100%" justifyContent="space-between">
              <Button
                onClick={handleClose}
                color="uncolored"
                variant="outlined"
                fullWidth={Device.isMobile}
                disabled={upserting || removing}
              >
                {t('cancel', { ns: 'cta' })}
              </Button>
              <Button
                loading={upserting || removing}
                fullWidth={Device.isMobile}
                onClick={handleSubmit}
                type="submit"
                variant="contained"
                color="success"
                disabled={!(formik.isValid && formik.dirty)}
              >
                {t('save', { ns: 'cta' })}
              </Button>
            </Stack>
          </DialogActions>
        </Stack>
      </Dialog>
    </>
  );
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});
